import { useState } from 'react';
import type { Me } from './me';

/**
 * Whether to show the user's profile photo, and what to do when it fails.
 *
 * ONE decision, shared by every avatar (the wide rail's account card, the mobile drawer's account
 * row, the Account page's profile card) so they cannot disagree: the photo is the recipient's own
 * self-photo (`Recipient.avatarUrl`) when the caller passes it, else the Wix identity `me.profileImage`,
 * and a load failure latches this browser back to the initials for the rest of the session rather than
 * leaving a broken-image glyph in a circle.
 *
 * `preferredUrl` is the recipient self-photo the user set in Settings — it wins over the identity photo
 * so a chosen self-photo shows everywhere the identity photo would. A caller with no recipient in hand
 * (the file-attribution mark) simply omits it and keeps the identity-photo behaviour.
 *
 * The latch is per-hook-instance on purpose — a failure in one avatar shouldn't blank the others,
 * and each remounts with a fresh attempt on the next navigation.
 */
export function useProfilePhoto(me: Me | null, preferredUrl?: string | null): { photoUrl: string | null; onPhotoError: () => void } {
  const [failed, setFailed] = useState(false);
  const resolved = (preferredUrl && preferredUrl.trim()) || me?.profileImage || '';
  return {
    photoUrl: resolved && !failed ? resolved : null,
    onPhotoError: () => setFailed(true),
  };
}
